import React, { useEffect, useRef, useState } from "react";
import { PreloaderPercent, PreloaderTracks } from "components/Preloader";
import { useIndexedDB } from "hooks/useIndexedDb";
import { Database } from "utils";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { PlayerVideoSC } from "./styled";
import { FAST_API_URL } from "api";
import { usePlaybackGate } from "hooks/usePlaybackGate";
import { originalPositionMs } from "utils/originalMedia";
const MIN_VIDEO_SIZE_MLS = 1000;

/**
 * How far the picture may drift from the sound before it is pulled back.
 *
 * The video has no clock of its own worth trusting: it is muted, it is started and stopped
 * independently, and it is now also stopped for every voicing that is still being prepared. Any of
 * those leaves it ahead of or behind the Russian audio, and nothing used to correct it, so the
 * error only ever grew. A quarter of a second is below what reads as out-of-sync on a talking head,
 * and correcting a muted video is invisible.
 */
const VIDEO_SYNC_TOLERANCE_MS = 250;

/** How often to check. Cheap: one comparison against the element the schedule already reads. */
const VIDEO_SYNC_TICK_MS = 500;

interface IPlayerVideoProps {
    children?: React.ReactNode;
}

export const Player: React.FC<IPlayerVideoProps> = ({ children }) => {
    const handles = useAudioHandlesContext();
    const state = useAudioStateContext();
    const videoRef = useRef<HTMLVideoElement>(null);
    /**
     * THE PICTURE WAITS TOO.
     *
     * This was the whole of "the Russian audio and the video just keep playing": `subscribeHold`
     * had exactly one subscriber in the codebase — the original's waveform — and holding never
     * touched `state.playing`, so the effect below never re-ran and the video played straight
     * through every wait.
     */
    const held = usePlaybackGate();
    const [canPlay, setCanPlay] = React.useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [videoBlobUrl, setVideoBlobUrl] = useState<string>();

    const { putValueAndClearOld, getValue, getAllValue, updateValue, deleteValue, isDBConnecting } =
        useIndexedDB(Database.name);

    const videoUrl = state.videoUrl
        ? `${FAST_API_URL}/download_rus?file_name=${state.videoUrl}`
        : "";

    useEffect(() => {
        const abortController = new AbortController();

        async function fetchVideo(url: string, signal: AbortSignal): Promise<Blob> {
            const response = await fetch(url, { signal });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status} ${response.statusText}`);
            }

            const contentLength = response.headers.get("Content-Length");
            const totalBytes = contentLength ? parseInt(contentLength, 10) : null;

            if (!response.body) {
                return await response.blob();
            }

            const reader = response.body.getReader();
            const chunks: Uint8Array[] = [];
            let receivedBytes = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                receivedBytes += value.length;
                if (totalBytes && totalBytes > 0) {
                    setLoadingProgress((receivedBytes / totalBytes) * 100);
                }
            }

            return new Blob(chunks);
        }

        async function getVideoFromDB() {
            console.log("state.video = ", Boolean(state.video), state.video, isDBConnecting);

            if (!state.mediaId) {
                return;
            }
            try {
                const response = await getValue(Database.videoTable, state.mediaId + state.folder);
                console.log("response: ", response);
                if (response && response.content && response.content.size > MIN_VIDEO_SIZE_MLS) {
                    setVideoBlobUrl(URL.createObjectURL(response.content));
                    setLoadingProgress(100);
                } else {
                    const url = videoUrl + (videoUrl.includes("?") ? "&" : "?") + "_t=" + Date.now();
                    let blob: Blob | undefined;

                    try {
                        blob = await fetchVideo(url, abortController.signal);
                    } catch (err: any) {
                        if (err.name === "AbortError") return;
                        console.error("Video download failed, retrying in 2s:", err.message || err);
                        await new Promise((r) => setTimeout(r, 2000));
                        if (abortController.signal.aborted) return;
                        blob = await fetchVideo(url, abortController.signal);
                    }

                    if (!blob || blob.size < MIN_VIDEO_SIZE_MLS) {
                        console.error("File downloaded is too small, size:", blob?.size);
                        return;
                    }

                    const blobUrl = URL.createObjectURL(blob);
                    console.log("Created blob URL:", blobUrl);

                    putValueAndClearOld(Database.videoTable, {
                        mediaId: state.mediaId,
                        name: state.mediaId,
                        content: blob,
                        id: state.mediaId + state.folder,
                    });
                    setVideoBlobUrl(blobUrl);
                    setLoadingProgress(100);
                }
            } catch (e: any) {
                if (e.name === "AbortError") return;
                console.error("Video download error:", e.message || e, e);
            }
        }

        if (!isDBConnecting && state.video && videoUrl && !videoBlobUrl) {
            getVideoFromDB();
        }

        return () => {
            abortController.abort();
        };
    }, [state.video, isDBConnecting, videoUrl, state.mediaId, state.folder]);

    useEffect(() => {
        return () => {
            if (videoBlobUrl) {
                URL.revokeObjectURL(videoBlobUrl);
            }
        };
    }, [videoBlobUrl]);

    // Play or pause the video with the lecture — and stand still while a voicing is being waited for
    useEffect(() => {
        if (videoRef.current && videoBlobUrl && canPlay) {
            if (state.playing && !held) {
                videoRef.current.play().catch((err) => console.log("play error", err));
            } else {
                videoRef.current.pause();
            }
        }
    }, [state.playing, held, videoBlobUrl, canPlay]);

    // videoBlobUrl/canPlay are in the deps because the rate is restored from localStorage before
    // the <video> exists (it is only rendered at loadingProgress === 100), and a one-shot effect
    // that fired before then left the picture running at 1.0 for the rest of the session.
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = state.playbackRate;
        }
    }, [state.playbackRate, videoBlobUrl, canPlay]);

    /**
     * THE PICTURE FOLLOWS THE SOUND. It is not driven, it is corrected.
     *
     * There used to be a single seek here, on `state.playOriginalStart`. It could not do the job
     * three ways over: it fired before the <video> existed and was simply lost; writing the same
     * value twice does not re-run an effect, so clicking the same cue again never moved the
     * picture; and nothing at all corrected the drift that every hold introduced. Following the
     * master clock covers all three, and needs no cooperation from whoever moved the playhead.
     */
    useEffect(() => {
        if (!videoBlobUrl || !canPlay) return;
        const sync = () => {
            const video = videoRef.current;
            const master = originalPositionMs();
            if (!video || master === null || !Number.isFinite(video.currentTime)) return;
            if (Math.abs(video.currentTime * 1000 - master) > VIDEO_SYNC_TOLERANCE_MS) {
                try {
                    video.currentTime = master / 1000;
                } catch {
                    /* not seekable yet; the next tick tries again */
                }
            }
        };
        sync();
        const timer = window.setInterval(sync, VIDEO_SYNC_TICK_MS);
        return () => window.clearInterval(timer);
    }, [videoBlobUrl, canPlay, held, state.playing, state.playOriginalStart]);

    useEffect(() => {
        setCanPlay(false);
    }, [videoBlobUrl]);

    useEffect(() => {
        if (videoRef.current && videoBlobUrl) {
            videoRef.current.load(); // Explicitly trigger video load
        }
    }, [videoBlobUrl]);

    return (
        <div className="c" style={{ width: "100%" }}>
            <PlayerVideoSC>
                {children}

                {Boolean(state.videoUrl) &&
                    Boolean(state["mp3"]) &&
                    Boolean(state.video) &&
                    Boolean(loadingProgress === 100) && (
                        <div className="x_player_video_sett0">
                            <div
                                className={`x_player_video_set_1 ${state.video === "sm" ? "active" : ""}`}
                                onClick={() => handles.setVideo("sm")}
                            >
                                <div className="ico">
                                    <div>crop_7_5</div>
                                </div>
                            </div>

                            <div
                                className={`x_player_video_set_1 ${state.video === "md" ? "active" : ""}`}
                                onClick={() => handles.setVideo("md")}
                            >
                                <div className="ico">
                                    <div>crop_5_4</div>
                                </div>
                            </div>

                            <div
                                className={`x_player_video_set_1 ${state.video === "full" ? "active" : ""}`}
                                onClick={() => handles.setVideo("full")}
                            >
                                <div className="ico">
                                    <div>fullscreen</div>
                                </div>
                            </div>
                        </div>
                    )}

                {Boolean(videoUrl) && loadingProgress < 100 && (
                    <PreloaderTracks>
                        {/* <PreloaderGif width={44} height={44} /> */}
                        {Boolean(state.video) && (
                            <PreloaderPercent>
                                Video is loading: {Math.round(loadingProgress)}%
                            </PreloaderPercent>
                        )}
                    </PreloaderTracks>
                )}

                {Boolean(videoUrl) && loadingProgress === 100 && (
                    <video
                        preload="auto"
                        style={{ zIndex: 0 }}
                        ref={videoRef}
                        className={`x_player_video x_player_video--${state.video}`}
                        muted
                        onCanPlay={() => {
                            console.log("setCanPlay");
                            setCanPlay(true);
                        }}
                    >
                        <source src={videoBlobUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                )}

                {!Boolean(loadingProgress === 100) && <div className={`x_player_video`}></div>}
            </PlayerVideoSC>
        </div>
    );
};
